'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { getCookie } from 'typescript-cookie';
import ContentStackImage from '@/components/customercenter-ui/common/contentStackImage/contentStackImage';
import NoImage from '@/components/customercenter-ui/svgs/noImage';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { formatZipCode } from '@/components/customercenter-ui/utils/formatUtils';
import { useAccount } from '@/frontastic';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { combinedKeyPressHandler, formatCurrency, getAgentName, getCookieValue } from '@/helpers/utils/siteUtils';
import {
  CustomerAddressPayload,
  LineItemAffected,
  SendTracerPayload,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, MAX_PAGE_SIZE, LINE_ITEMS_TABLE_KEYS, SEND_TRACER_TYPE } from '../../../../../constants/constant';
import { CustomFloatingSelect } from '../../customer-detail/components/customSelect';
import { DEFAULT_COUNTRY, PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../customer-detail/constants';
import { mapAddressData, transformShippingAddress } from '../../customer-detail/utils';
import { LineItems } from '../../order-details/types';
import { Button } from '../../ui/button';
import { Table, TableColumnConfig } from '../../ui/customTable/Table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../ui/use-toast';
import { maskPhoneNumber, maskPostalCode } from '../../utils/formatUtils';
import EditAddressModal from '../customerDetailAction/editAddress';

const SendTracerModal = ({
  Texts,
  orderItems,
  RecipientAddresses,
  customerId,
  addNewNotes,
  open,
  setOpen,
  isThirdPartyPayment,
  emailAddress,
}: {
  Texts: any;
  orderItems: LineItems;
  RecipientAddresses: any;
  customerId: string;
  addNewNotes: any;
  open: boolean;
  setOpen: any;
  isThirdPartyPayment?: boolean;
  emailAddress: string;
}) => {
  const LineItems =
    orderItems?.lineItems?.filter(
      (items) => items?.status !== 'Adjustment' && items?.postSubmissionIndo?.AvailableActions?.canSendTracer,
    ) ?? [];
  const { toast } = useToast();
  const [checked, setChecked] = useState(false);
  const { sendTracer, getCustomerAddresses } = useAccount();
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState<any>([]);
  const [selectedAddress, setSelectedAddress] = useState<any>({});
  const [itemError, setItemError] = useState({ error: false, message: '' });
  const [note, setNote] = useState('');
  const [error, setError] = useState(false);
  const params = useSearchParams();
  const [checks, setChecks] = useState([...Array(LineItems?.length).fill(false)]);
  const [openModal, setOpenModal] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const getAddresses = async () => {
    if (addresses?.length === 0) {
      setLoading(true);
      const payload = {
        query: {
          omsCustomerId: customerId,
          pageSize: MAX_PAGE_SIZE,
          divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
        },
      } as CustomerAddressPayload;
      const addresses = await getCustomerAddresses(payload);
      const RecipientAddress = addresses?.RecipientAddresses ?? [];
      setAddresses([RecipientAddresses, ...RecipientAddress]);
      setSelectedAddress(RecipientAddresses);
      setLoading(false);
    }
  };

  const setAddressLocally = (address: any) => {
    const mappedAddress = mapAddressData({ ...address, addressId: selectedAddress?.HeaderInfo?.addressId });
    setSelectedAddress(mappedAddress);
  };

  useEffect(() => {
    getAddresses();
  }, []);

  const resetItemError = () => {
    setItemError({ error: false, message: '' });
  };

  const tableHeaders: TableColumnConfig<any>[] = [
    {
      key: LINE_ITEMS_TABLE_KEYS.CHECKBOX,
      header: (
        <input
          className={`${itemError?.error ? 'border-scarletRed' : ''}`}
          type="checkbox"
          id="all"
          name="all"
          checked={checked}
          onChange={(e) => {
            resetItemError();
            if (e?.target?.checked) {
              setChecked(true);
              setChecks([...Array(LineItems?.length).fill(true)]);
            } else {
              setChecks([...Array(LineItems?.length).fill(false)]);
              setChecked(false);
            }
          }}
        />
      ),
      render: (data, rowData, rowIndex) => (
        <input
          type="checkbox"
          className={`${itemError?.error ? 'border-scarletRed' : ''}`}
          id={rowData?.lineSequenceNumber}
          name={rowData?.itemNumber}
          value={rowData?.lineSequenceNumber}
          checked={checks[rowIndex ?? 0]}
          onChange={(e) => {
            resetItemError();
            if (e.target.checked) {
              const newChecks = [...checks];
              newChecks[rowIndex ?? 0] = true;
              setChecks(newChecks);
            } else {
              const newChecks = [...checks];
              newChecks[rowIndex ?? 0] = false;
              setChecks(newChecks);
              setChecked(false);
            }
          }}
        />
      ),
    },
    {
      key: LINE_ITEMS_TABLE_KEYS.ITEM_DESCRIPTION,
      header: Texts.productDetails,
      render: (data, rowData) => (
        <section className="flex-col-2 flex gap-4">
          <div>
            {rowData?.image ? (
              <ContentStackImage
                className="w-full"
                src={rowData?.image}
                imageWidth={90}
                imageHeight={90}
                alt={`Product Image ${rowData?.vSku}`}
                loading="eager"
              />
            ) : (
              <NoImage className="h-20 w-20" />
            )}
          </div>
          <div>
            <p className="font-bold text-black">{rowData?.itemDescription}</p>
            <p>
              {Texts.itemNumber}:{rowData?.itemNumber}
            </p>
            <p>{rowData?.color}</p>
            <p className="font-bold text-black">{formatCurrency(rowData?.itemUnitPrice)}</p>
          </div>
        </section>
      ),
    },
    {
      key: LINE_ITEMS_TABLE_KEYS.QUANTITY,
      header: Texts.quantity,
      render: (data, row) => {
        const lineSequenceNumber = row?.lineSequenceNumber;

        const quantity =
          lineSequenceNumber && quantities[lineSequenceNumber] !== undefined
            ? quantities[lineSequenceNumber]
            : String(data);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          if (/^\d*$/.test(value)) {
            setQuantities((prev) => ({
              ...prev,
              [lineSequenceNumber]: value,
            }));
          }
        };

        const handleBlur = () => {
          const value = Number(quantities[lineSequenceNumber]);

          // If empty, reset to default
          if (quantities[lineSequenceNumber] === '') {
            setQuantities((prev) => ({
              ...prev,
              [lineSequenceNumber]: String(data),
            }));
            return;
          }

          // Less than 1 → reset to 1
          if (value < 1) {
            setQuantities((prev) => ({
              ...prev,
              [lineSequenceNumber]: '1',
            }));
            return;
          }

          // Greater than available (data) → reset to max
          if (value > Number(data)) {
            setQuantities((prev) => ({
              ...prev,
              [lineSequenceNumber]: String(data),
            }));
          }
        };

        return (
          <input
            className="gbunmask h-[38px] w-[37px] rounded-lg border-[#B6B6B8]"
            value={quantity}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        );
      },
    },
  ];
  const onSubmit = async () => {
    const LineItemsAffected =
      LineItems?.map((item, index) => {
        if (checks[index]) {
          return {
            itemId: item?.itemNumber,
            itemDescription: item?.itemDescription,
            itemUnitPrice: item?.itemUnitPrice ?? 0,
            quantity: Number(quantities[item?.lineSequenceNumber] ?? item?.quantity ?? 1),
            lineShippedDate: item?.lineShippedDate,
            lineSequenceNumber: item?.lineSequenceNumber,
          };
        }
      })?.filter((item) => item) ?? ([] as LineItemAffected[]);
    const caseId = getCookie('caseId');
    let canSendTracer = true;
    if (!note) {
      canSendTracer = false;
      setError(true);
    }
    if (LineItemsAffected?.length === 0) {
      canSendTracer = false;
      setItemError({ error: true, message: Texts.itemErrorMessage });
    }
    if (!canSendTracer) return;
    setLoading(true);

    const sendTracerPayLoad = {
      query: {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        X_INITIATING_AGENT: getAgentName(),
      },
      data: {
        requestType: SEND_TRACER_TYPE.REQUEST_TYPE,
        sendVia: SEND_TRACER_TYPE.SEND_VIA_TYPE,
        customerId: customerId,
        omsCustomerId: customerId,
        Address: {
          firstName: selectedAddress?.PersonInfo?.firstName,
          lastName: selectedAddress?.PersonInfo?.lastName,
          line1: selectedAddress?.LocationInfo?.line1,
          ...(selectedAddress?.LocationInfo?.line2 && { line2: selectedAddress?.LocationInfo?.line2 }),
          city: selectedAddress?.LocationInfo?.city,
          state: selectedAddress?.LocationInfo?.state,
          zip: formatZipCode(selectedAddress?.LocationInfo?.zip),
          country: DEFAULT_COUNTRY,
          ...(emailAddress && { emailAddress }),
        },
        OrderDetails: {
          orderId: orderItems?.orderId,
          LineItemsAffected: LineItemsAffected,
        },
        reasonNote: note,
        caseId: caseId,
      },
    } as SendTracerPayload;
    await sendTracer(sendTracerPayLoad).then((response) => {
      setLoading(false);
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        setOpen(false);
        SuccessNotification(toast, {
          description: Texts?.tracerSentSuccessfully,
          action: <SuccessIcon className="h-5 w-5" />,
        });
        addNewNotes();
      } else {
        ErrorNotification(toast, { description: Texts?.tracerSentError, action: <ErrorIcon className="h-5 w-5" /> });
      }
    });
  };

  const setDefaultValues = () => {
    setError(false);
    setNote('');
    setChecked(false);
    setChecks([...Array(LineItems?.length).fill(false)]);
  };
  const handleChangeAddress = (addressId: string) => {
    const selectedAddress = addresses?.filter((items: any) => items?.HeaderInfo?.addressId === addressId);
    setSelectedAddress(selectedAddress[0]);
  };

  return (
    <>
      {isThirdPartyPayment ? (
        <Dialog
          open={open}
          onOpenChange={(value) => {
            setOpen(value);
            setDefaultValues();
          }}
        >
          <DialogContent className="m-0 max-w-[717px] bg-white p-0">
            <DialogHeader className="bg-gray-300">
              <DialogTitle className="text-xl font-bold">
                <section>
                  <div className="px-4 py-2 text-xl font-bold">{Texts.sendTracer}</div>
                </section>
              </DialogTitle>
            </DialogHeader>
            <section className="bg-white">
              <div className=" px-8 pb-4 text-xl font-medium text-scarletRed">
                {Texts.sendTracerThirdPartyPaymentMessage}
              </div>
            </section>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog
          open={open}
          onOpenChange={(value) => {
            setOpen(value);
            setDefaultValues();
          }}
        >
          <DialogContent className="custom-scrollbar max-h-[90%] max-w-[717px] overflow-y-auto bg-white sm:rounded-md ">
            {loading && <Loader />}
            <DialogHeader>
              {/* chng-text-25px */}
              <DialogTitle className="text-2xl font-bold">
                <section>
                  <div className="text-2xl font-bold">{Texts.sendTracer}</div>
                  <div className="text-xs font-normal">{Texts.requiredFields}</div>
                </section>
              </DialogTitle>
            </DialogHeader>
            <section>
              <CustomFloatingSelect
                label={'Send via mailing address'}
                options={addresses?.map((items: any) => (
                  <option
                    key={items?.HeaderInfo?.addressId}
                    value={items?.HeaderInfo?.addressId}
                    className="custom-scrollbar"
                  >
                    <p className="gbmask">
                      {items?.PersonInfo?.firstName} {items?.PersonInfo?.lastName}
                    </p>
                  </option>
                ))}
                defaultValue={'1'}
                placeholder={'Send via mailing address'}
                onChange={(id) => handleChangeAddress(id)}
                className="w-[400px]"
              />
              <div className="mt-6 px-2">
                <div className="gbmask">
                  <p>
                    {selectedAddress?.PersonInfo?.firstName} {selectedAddress?.PersonInfo?.lastName}
                  </p>
                  <p>{selectedAddress?.LocationInfo?.line1}</p>
                  <p>
                    {selectedAddress?.LocationInfo?.city}, {selectedAddress?.LocationInfo?.state}{' '}
                    {maskPostalCode(selectedAddress?.LocationInfo?.zip ?? '')}
                  </p>
                  <p className="my-2">{maskPhoneNumber(selectedAddress?.LocationInfo?.phoneNumber ?? '')}</p>
                </div>

                <button className="text-blueLight underline" onClick={() => setOpenModal(true)}>
                  {Texts.editAddress}
                </button>
                {openModal && (
                  <EditAddressModal
                    Texts={Texts}
                    data={{ ...(transformShippingAddress([selectedAddress])?.[0] ?? {}) }}
                    setSelectedBillingAddress={setAddressLocally}
                    open={openModal}
                    setOpen={setOpenModal}
                    makeAPIcall={false}
                  />
                )}
              </div>
            </section>
            <div id="checkboxes">
              <Table columns={tableHeaders} data={LineItems} alignTextTop designTable={false} />
              <div>
                {itemError?.error ? <div className="font-semibold text-scarletRed">{itemError?.message}</div> : <></>}
              </div>
            </div>
            <section>
              <section className="h-[81px]">
                <Textarea
                  placeholder="*Note"
                  className={`${error ? 'border-scarletRed text-scarletRed' : ''} h-full w-full resize-none`}
                  maxLength={2500}
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    setError(false);
                  }}
                  onKeyPress={combinedKeyPressHandler}
                />
              </section>
              {error ? <div className="font-semibold text-scarletRed">{Texts.enterNote}</div> : <></>}
            </section>

            <section className="flex justify-start gap-4">
              <Button className="rounded-md bg-deepBlue p-4 text-white" onClick={() => onSubmit()}>
                {Texts.sendTracer}
              </Button>
              <Button className="p-0 underline" onClick={() => setOpen(false)}>
                {Texts.cancelButton}
              </Button>
            </section>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default SendTracerModal;
